import { computed, ref } from 'fine-jsx'

import { NODE_FIELD_FLAGS } from '#constants'
import type * as pub from '#core'
import type { ClipTime, ClipTimeRational } from '#core'
import { NodeUpdateEvent } from '#events'
import type { KeyofUnion } from '#internal'
import type { BaseClip } from '#nodes'

import { TrackChild } from '../../nodes/track-child.ts'
import type { ViewType } from '../document-view.ts'
import { NodeView } from '../node-view.ts'

import type { EditDocument, ViewTypeMap } from './edit-document.ts'
import type { EditNodeLink } from './edit-node-link.ts'

declare module 'webgl-video-editor' {
  export interface BaseNode {
    readonly [IS_EDIT_VIEW]?: boolean
  }
}

const IS_EDIT_VIEW = '_is_edit_view'

const viewProxies = new WeakMap<EditView<any>, EditView.ProxyOf<pub.AnyNode>>()

export namespace EditView {
  export type ProxyOf<T extends pub.AnyNode> = EditView<T> & T
  export type Timeline = ProxyOf<pub.Timeline>
  export type VideoTrack = ProxyOf<pub.VideoTrack>
  export type AudioTrack = ProxyOf<pub.AudioTrack>
  export type AnyTrack = VideoTrack | AudioTrack
  export type VideoClip = EditClip<pub.VideoClip> & ProxyOf<pub.VideoClip>
  export type AudioClip = EditClip<pub.AudioClip> & ProxyOf<pub.AudioClip>
  export type ImageClip = EditClip<pub.ImageClip> & ProxyOf<pub.ImageClip>
  export type TextClip = EditClip<pub.TextClip> & ProxyOf<pub.TextClip>

  export type AnyClip = VideoClip | AudioClip | ImageClip | TextClip
  export type AnyVideoClip = VideoClip | ImageClip | TextClip
  export type AnyTrackChild = AnyClip
}

export class EditView<T extends pub.AnyNode> extends NodeView<EditDocument, T> {
  static BOUND_METHODS = new Set<KeyofUnion<pub.AnyNode | NodeView<EditDocument, any>>>([
    'delete',
    'dispose',
    'move',
    'remove',
  ])

  readonly [IS_EDIT_VIEW]? = true as boolean
  readonly _boundMethods: Record<string | symbol, (...args: unknown[]) => unknown>

  readonly _editedProps = ref<Partial<T>>()

  _isEnding = false

  declare parent?: ViewType<ViewTypeMap, T['parent']>
  declare prev: ViewType<ViewTypeMap, T['prev']>
  declare next: ViewType<ViewTypeMap, T['next']>
  declare head?: T extends pub.ParentNode<infer TChild> ? ViewType<ViewTypeMap, TChild> : never
  declare tail?: T extends pub.ParentNode<infer TChild> ? ViewType<ViewTypeMap, TChild> : never
  declare children?: T extends pub.ParentNode<infer TChild> ? ViewType<ViewTypeMap, TChild>[] : never

  get link(): T extends EditNodeLink.Linkable ? EditNodeLink<T> | undefined : never {
    const { link } = this.original as EditNodeLink.Linkable
    return (link && this.docView.links.get(link.id)) as never
  }

  _applyEdits = this._end.bind(this, true)
  _dropEdits = this._end.bind(this, false)

  constructor(editDoc: EditDocument, original: T) {
    super(editDoc, original)

    const Constructor = this.constructor as typeof EditView
    const methods: typeof this._boundMethods = {}

    Constructor.BOUND_METHODS.forEach((key) => {
      methods[key] = (original as unknown as typeof methods)[key].bind(original)
    })

    this._boundMethods = methods
  }

  _startEditing(keys: (keyof T)[]): void {
    if (this._editedProps.value) throw new Error('Already editing node.')

    const { original } = this
    const obj: Partial<T> = {}
    keys.forEach((key) => void (obj[key] = original[key]))
    this._editedProps.value = obj
  }

  _end(shouldApply: boolean): void {
    if (this.isDisposed) return

    this._isEnding = true

    const editedProps = this._editedProps.value
    const { original, docView } = this

    for (const key in editedProps)
      if (Object.hasOwn(editedProps, key)) {
        const editValue = editedProps[key]

        if (shouldApply) (original as any)[key] = editValue
        else {
          const originalValue = (original as any)[key]

          if (JSON.stringify(originalValue) !== JSON.stringify(editValue))
            docView.emit(new NodeUpdateEvent(docView._getNode(original), key as any, editValue))
        }
      }

    this._editedProps.value = undefined
    this._isEnding = false
  }

  static proxy = <T extends pub.AnyNode>(view: EditView<T> | EditClip<pub.AnyClip>): EditView.ProxyOf<T> => {
    const proxy = new Proxy<EditView<any>>(view, nodeHandler) as EditView.ProxyOf<T>
    viewProxies.set(view, proxy as any)
    return proxy
  }
}

const nodeHandler: ProxyHandler<EditView<pub.AnyNode>> = {
  get(target, key, receiver) {
    const { original } = target

    switch (key) {
      case 'original':
        return original

      case 'doc':
      case 'docView':
        return target.docView

      // node properties
      case 'prev':
      case 'next':
      case 'parent':
      case 'head':
      case 'tail':
        return target.docView._getNode((original as pub.AnyParentNode)[key])

      // view properties and methods
      case '_move':
      case '_update':
      case '_startEditing':
      case '_applyEdits':
      case '_dropEdits':
      case '_editedProps':
      case '_isEnding':
        // eslint-disable-next-line @typescript-eslint/unbound-method -- expected to only be called on edit node
        return target[key]

      // links
      case 'link':
        return target.link
      case 'linkedAudio':
      case 'linkedVideo':
        return target.docView._getNode((original as pub.VideoClip & pub.AudioClip)[key])

      // node array properties
      case 'children':
        return (original as pub.AnyParentNode)[key].map((node) => target.docView._getNode(node))

      // clip time properties and methods
      case 'time':
      case 'presentationTime':
      case 'playableTime':
      case '_computeTimeRational':
      case '_computePresentationTime':
      case '_computePlayableTime':
      case '_computeIsInPresentationTime':
      case '_computeIsInPlayableTime':
        return Reflect.get(original, key, receiver)

      case '_timeRational':
      case '_time':
      case '_presentationTime':
      case '_playableTime':
      case '_isInPresentationTime':
      case '_isInPlayableTime':
        return (target as EditClip<any>)[key]

      case 'constructor':
        return original[key]

      case IS_EDIT_VIEW:
        return true
    }

    const boundMethod = (target._boundMethods as any)[key] as ((...args: any[]) => unknown) | undefined

    if (boundMethod) return boundMethod

    if (original.isTrackChild()) {
      if (key === 'timeRational') return (target as EditView.AnyTrackChild)._timeRational.value

      if (key === 'gap') {
        const dragGap = target.docView.clipDrag.getAdjustedGap(receiver)
        if (dragGap) return dragGap
      }
    }

    // avoid tracking editing status for non editable fields
    const fieldDef = original._fieldsByKey()[key as any]
    if (fieldDef && !(fieldDef.flags & NODE_FIELD_FLAGS.Constant)) {
      const editedProps = target._editedProps.value
      if (editedProps && key in editedProps) return editedProps[key as keyof typeof original]
    }

    return Reflect.get(original, key, original)
  },
  set(target, key, value, receiver) {
    const editedProps: Record<string | symbol, unknown> | undefined = target._editedProps.value
    const { original } = target

    if (editedProps && key in editedProps) {
      const oldValue = editedProps[key]
      target._editedProps.value = { ...editedProps, [key]: value }
      target.docView._emit(new NodeUpdateEvent(receiver, key as any, oldValue))
      return true
    } else if (original.animationMap.has(key as string)) {
      ;(original as any)[key] = value
      return true
    }

    return Reflect.set(original, key, value, original)
  },
  has(target, key) {
    return Reflect.has(target.original, key)
  },
  ownKeys(target) {
    const keys = Reflect.ownKeys(target.original)
    keys.push(IS_EDIT_VIEW)
    return keys
  },
  getPrototypeOf(target) {
    return Reflect.getPrototypeOf(target.original)
  },
}

abstract class EditTrackChild<T extends pub.AnyClip> extends EditView<T> {
  /* eslint-disable @typescript-eslint/unbound-method -- Reflect.apply */
  _timeRational = computed((): ClipTimeRational =>
    Reflect.apply(
      (this.original as unknown as TrackChild<T>)._computeTimeRational,
      viewProxies.get(this),
      [],
    ),
  )
  _time = computed((): ClipTime => TrackChild.clipTimeRationalToDecimal(this._timeRational.value))
}

export class EditClip<T extends pub.AnyClip> extends EditTrackChild<T> {
  _presentationTime = computed((): ClipTime =>
    Reflect.apply(
      (this.original as unknown as BaseClip<T>)._computePresentationTime,
      viewProxies.get(this),
      [],
    ),
  )
  _playableTime = computed((): ClipTime =>
    Reflect.apply((this.original as unknown as BaseClip<T>)._computePlayableTime, viewProxies.get(this), []),
  )
  _isInPresentationTime = computed((): ClipTime =>
    Reflect.apply(
      (this.original as unknown as BaseClip<T>)._computePresentationTime,
      viewProxies.get(this),
      [],
    ),
  )
  _isInPlayableTime = computed((): ClipTime =>
    Reflect.apply((this.original as unknown as BaseClip<T>)._computePlayableTime, viewProxies.get(this), []),
  )
  /* eslint-enable @typescript-eslint/unbound-method */
}
